import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { LoadingOutlined } from "@ant-design/icons";
import { Divider, Input, Select, Spin, Tabs, Tag } from "antd";
import "../../assets/css/outbound.css";
import { AppContext } from "../../context/AppContext";
import useHttp from "../../hooks/useHttp";
import useTemplates from "../../hooks/useTemplates";
import { getLabelForTemplate, RefreshButton } from "../utils/CommonVessels";
import { PLAIN_TEMPLATE_OBJECT, TEMPLATE_TYPE_LABELS } from "../../constants/AppConstants";
import { useTemplateStore } from "./templateStore";

export const SavedTemplatesSelect = React.memo((props) => {
  const {
    setTemplateId,
    templateId,
    setMessageAreaLoading,
    handleMessage,
    tabIndex,
    moduleName,
    showPreview,
    fetchOnlyByModule,
    handleMedia,
    showOnlyPopup,
    suffixIcon,
    shrinkView,
    open,
    setOpen,
    groupBy = "moduleName",
    setPreviewResponse,
    toNumber = "",
    chatView,
    integrationId,
    previewParams = {},
    placement = null,
    dropdownStyle = { width: 300 },
    fetchAllTemplates,
    isRcsEnabled = false,
    phoneNumber = "",
  } = props;

  const [appProperties] = useContext(AppContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const { fetchSavedTemplates, fetchSavedTemplatesByModule } = useTemplates();
  const { fetchData } = useHttp();
  const { associatedModule, associatedId } = previewParams;

  const { templates, templateLoading, fetchTemplates, refreshTemplates } = useTemplateStore();

  // Memoize the fetch parameters to prevent unnecessary re-renders
  const fetchParams = useMemo(
    () => ({
      templatesSelectOpen: true,
      refresh: false,
      appProperties,
      moduleName,
      integrationId,
      fetchOnlyByModule: fetchOnlyByModule || chatView || shrinkView,
      fetchSavedTemplates,
      fetchSavedTemplatesByModule,
      isRcsEnabled,
      phoneNumber,
      fetchAllTemplates,
    }),
    [appProperties, moduleName, integrationId, fetchOnlyByModule, chatView, shrinkView, fetchSavedTemplates, fetchSavedTemplatesByModule, isRcsEnabled, phoneNumber, fetchAllTemplates]
  );

  function redirectToAddTemplate() {
    appProperties?.controller?.getUrlParamsForRedirection(true, appProperties).then(function (redirectionUrl) {
      if (moduleName) {
        let templateUrlLink = "/templates?" + redirectionUrl + "&resource=" + (moduleName ?? "") + "&openTemplate=true";
        window.open(templateUrlLink, "_blank");
      } else {
        appProperties?.controller?.getAssociatedObjectType(appProperties).then(function (moduleName) {
          let templateUrlLink = "/templates?" + redirectionUrl + "&resource=" + (moduleName ?? "") + "&openTemplate=true";
          window.open(templateUrlLink, "_blank");
        });
      }
    });
  }

  const handleOnChange = useCallback(
    (v) => {
      setOpen(false);
      setTemplateId(v);
      let selectedTemplateEntity = templates.find((item) => item.templateId.toLowerCase().includes(v));
      if (showPreview) {
        previewTemplate(v, selectedTemplateEntity?.integId, associatedModule, associatedId);
      } else {
        setPreviewResponse && setPreviewResponse(selectedTemplateEntity || null);
        handleMessage && handleMessage(selectedTemplateEntity?.content || "");
      }
      handleMedia(selectedTemplateEntity?.mediaFileList || []);
    },
    [setTemplateId, templates, associatedModule, associatedId, handleMedia]
  );

  useEffect(() => {
    if (appProperties?.osyncId) {
      fetchTemplates(fetchParams);
    }
  }, [appProperties?.osyncId, fetchParams, fetchTemplates]);

  const previewTemplate = useCallback(
    async (templateId, integId = appProperties.randomIntegId, associatedModule, associatedId) => {
      if (templateId !== "add::::Templates") {
        setMessageAreaLoading(true);
        if (!associatedModule) associatedModule = await appProperties.controller.getAssociatedObjectType(appProperties);
        if (!associatedId) associatedId = await appProperties.controller.getAssociatedObjectId(appProperties);
        if (!showPreview) {
          associatedId = "";
          associatedModule = "";
        }

        fetchData(
          `omessage/${integId}/template/${templateId}/preview?associatedObjectId=${associatedId ?? ""}&associatedObjectType=${
            associatedModule ?? ""
          }&phone=${toNumber && !chatView ? encodeURIComponent(toNumber) : ""}`,
          "GET",
          null,
          appProperties
        )
          .then((response) => {
            const responseData = JSON.parse(response);
            if (responseData?.data?.data || responseData?.data?.mediaFileList || responseData?.data?.components) {
              setPreviewResponse && setPreviewResponse(responseData?.data);
              handleMessage(responseData?.data?.data || "");
            }
          })
          .catch((error) => {})
          .finally(() => {
            setMessageAreaLoading(false);
          });
      }
    },
    [appProperties, showPreview, toNumber, chatView, setMessageAreaLoading, setPreviewResponse, handleMessage, fetchData]
  );

  const mapOptions = useCallback(
    (listOfOptions) => {
      if (!groupBy) {
        return listOfOptions.map((option) => {
          return option;
        });
      }

      const groupedItems = {};

      // Group items by category

      listOfOptions.forEach((option) => {
        const generalTemplateKey = PLAIN_TEMPLATE_OBJECT.value;
        const groupByKey = option[groupBy] || generalTemplateKey;

        if (!moduleName || [generalTemplateKey, moduleName].includes(groupByKey)) {
          if (!groupedItems[groupByKey]) {
            groupedItems[groupByKey] = [];
          }
          groupedItems[groupByKey].push(option);
        }
      });

      // Create the final list in the desired format

      const groupedOptions = Object.keys(groupedItems).map((category) => ({
        label: <Tag className="select-group-tag">{category === PLAIN_TEMPLATE_OBJECT.value ? PLAIN_TEMPLATE_OBJECT.label : category}</Tag>,
        title: category,
        options: groupedItems[category],
      }));
      return groupedOptions;
    },
    [groupBy, moduleName]
  );

  // "All" and "SMS/WhatsApp" always show; WABA and RCS only when present in the store-filtered list
  const availableTypeTabs = useMemo(() => {
    const presentTypes = new Set(templates.map((t) => t.templateType).filter(Boolean));
    return Object.keys(TEMPLATE_TYPE_LABELS)
        .filter((key) => key === "all" || key === "application" || presentTypes.has(key))
        .map((key) => ({ key, label: TEMPLATE_TYPE_LABELS[key] }));
  }, [templates]);

  useEffect(() => {
    if (typeFilter !== "all" && !availableTypeTabs.some((t) => t.key === typeFilter)) {
      setTypeFilter("all");
    }
  }, [availableTypeTabs, typeFilter]);

  let templatesOption = useMemo(() => {
    let savedTemplatesListWithEyeLabel = [];
    if (templates) {
      templates.forEach((item) => {
        const matchesSearch = item.friendlyName.toLowerCase().includes(searchTerm.toLowerCase());
        const effectiveType = item.templateType || "application";
        const matchesType = typeFilter === "all" || effectiveType === typeFilter;
        if (matchesSearch && matchesType) {
          item.value = item.templateId;
          item.label = getLabelForTemplate(item?.friendlyName, item?.templateType, item?.content, item?.components);
          savedTemplatesListWithEyeLabel.push(item);
        }
      });
    }
    return savedTemplatesListWithEyeLabel;
  }, [templates, searchTerm, typeFilter]);

  const getSuffixIcon = useCallback(() => {
    if (showOnlyPopup) {
      return <></>;
    }

    if (templateLoading) {
      return <div className="actionIconsSprite dropDownLoading" />;
    }

    return suffixIcon ?? <div className="actionIconsSprite templatesDropdownIcon" />;
  }, [showOnlyPopup, templateLoading, suffixIcon]);

  const handleDropDownVisibleChange = useCallback(
    (visible) => {
      setOpen && setOpen(visible);
    },
    [setOpen]
  );

  const templateSearchInputRef = useRef(null);

  useEffect(() => {
    if (open && templateSearchInputRef.current) {
      templateSearchInputRef.current.focus({
        preventScroll: true,
      });
    }
  }, [open, templateSearchInputRef.current, templates]);

  const selectedTemplateId = useMemo(() => {
    const matchedTemplate = templates.find((item) => item.templateId === templateId);
    return matchedTemplate?.templateId || null;
  }, [templates, templateId]);

  return (
    <Select
      tabIndex={tabIndex}
      variant="borderless"
      className={`savedTemplateSelect ${showOnlyPopup ? "hs-select-show-only-popup" : ""} hs-saved-template-select w-100`}
      open={open}
      dropdownStyle={dropdownStyle}
      placeholder={showOnlyPopup ? null : <div className="hs-fs-small">Choose template</div>}
      data-cy="choose-template"
      value={selectedTemplateId}
      placement={placement}
      options={mapOptions(templatesOption)}
      optionFilterProp="children"
      filterOption={(input, option) => (option?.label ?? "").toString().includes(input)}
      suffixIcon={getSuffixIcon()}
      filterSort={(optionA, optionB) =>
        (optionA?.label ?? "")
          .toString()
          .toLowerCase()
          .localeCompare((optionB?.label ?? "").toString().toLowerCase())
      }
      // onBlur={() => {
      //   handleDropDownVisibleChange(false);
      // }}
      onDropdownVisibleChange={handleDropDownVisibleChange}
      dropdownRender={(menu) => (
        <>
          <div className="d-flex align-items-center justify-content-between px-2 pt-2 pb-0">
            <span className="fw-bold">Templates</span>
            <div className="d-flex align-items-center gap-1">
              <RefreshButton onClickFunction={() => refreshTemplates(fetchParams)} loading={templateLoading} />
              <div onClick={redirectToAddTemplate} className="btn p-0 ms-1 mb-1">
                <div className="actionIconsSprite addTemplatePlusIcon" data-cy="add-template-plus-icon" />
              </div>
            </div>
          </div>
          <Tabs
            activeKey={typeFilter}
            onChange={(key) => setTypeFilter(key)}
            className="custom-tabs saved-templates-tabs px-2 mb-0"
            size="small"
            items={availableTypeTabs.map(({ key, label }) => ({
              key,
              label: <span className="hs-fs-13">{label}</span>,
            }))}
          />
          <div className="py-1">
            <Input
              placeholder="Search templates"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="borderless"
              ref={templateSearchInputRef}
            />
          </div>
          <Divider className="mt-0 mb-0" />
          <div data-cy="saved-template-container hs-h-150-px">
            <Spin indicator={<LoadingOutlined spin />} spinning={templateLoading}>
              {menu}
            </Spin>
          </div>
        </>
      )}
      onSelect={handleOnChange}
      notFoundContent={
        <div className="hs-h-150-px d-grid align-items-center justify-content-center">
          {templateLoading ? <LoadingOutlined spin /> : "No templates!"}
        </div>
      }
      listHeight={150}
    />
  );
});

export default SavedTemplatesSelect;
